import { Box, CircularProgress, Stack, TablePagination, Typography } from '@mui/material';
import { isChart, isExpandableTable, isQuoteList } from 'constants/graphql-typenames';
import { useTablesInput } from 'providers/TablesInputProvider';
import { lazy, ReactNode, Suspense } from 'react';
import { ROWS_PER_PAGE } from 'services/api-constants';
import { EmptyTableState } from 'components/EmptyTableState';
import {
  ExpandableTable as ExpandableTableType,
  TablesScreen as TablesScreenType,
  ChartData,
  DataDisplay,
  TableGroup as TableGroupType,
} from 'generated/graphql';
import QuoteListView from './Quotes/QuoteListView';
import TableWrapper from './TableWrapper';
import { useTable } from './useTableNodes';

const WeekSelector = lazy(() => import('components/WeekSelector'));
const ChartContainer = lazy(() => import('components/ChartContainer'));

/** Stands in for the page arrows while the next page loads. */
const PageLoadingActions = () => (
  <CircularProgress
    size={24}
    color="secondary"
    sx={(theme) => ({
      display: 'flex',
      margin: theme.spacing(1, 2),
    })}
  />
);

const ExpandableTableView = ({
  table,
  dataDisplay,
  screen,
  isRevalidating = false,
  tabIndex,
  tableIndex,
  tableGroupIndex,
  onRefresh,
  onScreenUpdate = () => {},
  header,
}: {
  table: ExpandableTableType;
  dataDisplay: DataDisplay[];
  screen?: TablesScreenType;
  isRevalidating?: boolean;
  tabIndex: number;
  tableIndex: number;
  tableGroupIndex: number;
  onRefresh(): void;
  onScreenUpdate?(screen: TablesScreenType): void;
  header: (actions: ReactNode) => ReactNode;
}) => {
  return (
    <TableWrapper
      key={table.title}
      table={table}
      tables={dataDisplay as ExpandableTableType[]}
      tabIndex={tabIndex}
      tableGroupIndex={tableGroupIndex}
      index={tableIndex}
      isRevalidating={isRevalidating}
      onRefresh={onRefresh}
      onScreenUpdate={onScreenUpdate}
      screen={screen}
      header={header}
    />
  );
};

const ChartView = ({ dataDisplay }: { dataDisplay: DataDisplay[] }) => {
  const { dateRange, setDateRange, resetDateRange } = useTablesInput();

  if (!dataDisplay.length) return null;

  const chartData = dataDisplay[0] as ChartData;

  const chartDateRange = dateRange ? { ...dateRange, onDatesChange: setDateRange, onReset: resetDateRange } : undefined;

  return (
    <Box
      sx={(theme) => ({
        marginTop: theme.spacing(2),
        '& #chartContainer': { backgroundColor: theme.palette.common.white },
        '& #chartSection': { marginTop: theme.spacing(4) },
      })}
    >
      <Suspense fallback={null}>
        <ChartContainer
          title={chartData.title}
          charts={[chartData]}
          toolTip={chartData.toolTip}
          placeholder={chartData.placeholder && <EmptyTableState placeholder={chartData.placeholder} iconSize={40} />}
          quickViewFilter={
            <Stack
              direction="row"
              sx={{
                justifyContent: 'end',
              }}
            >
              {chartDateRange && <WeekSelector dateRange={chartDateRange} range={6} />}
            </Stack>
          }
        />
      </Suspense>
    </Box>
  );
};

/** Whether data shows nothing, for the screen to show its empty state in its place. */
export const isDataEmpty = (data: DataDisplay[]) => {
  const tables = data.filter(isExpandableTable);
  const charts = data.filter(isChart);
  return (
    data.length === 0 ||
    (tables.length > 0 &&
      tables.every((table) => table.groups[0]?.rows.length === 0 && table.tableActions.length === 0)) ||
    (charts.length > 0 && charts.every((chart) => chart.periods[0]?.multiSeries.length === 0))
  );
};

export interface TableGroupProps {
  screen?: TablesScreenType;
  tableGroup?: TableGroupType;
  isFetching?: boolean;
  isRevalidating?: boolean;
  enableTabs?: boolean;
  tabIndex: number;
  tableGroupIndex: number;
  onRefresh(): void;
  onScreenUpdate?(screen: TablesScreenType): void;
}
interface TableGroupContentProps extends TableGroupProps {
  // Empty for a chart, which doesn't page.
  identifier: string;
  // The table's first page, as its group loaded it.
  groupData: DataDisplay;
  dataDisplay?: DataDisplay[];
  tableIndex: number;
}

const TableGroupContent = ({
  screen,
  identifier,
  groupData,
  dataDisplay = [],
  isRevalidating = false,
  tabIndex,
  tableIndex,
  tableGroupIndex,
  onRefresh,
  onScreenUpdate = () => {},
}: TableGroupContentProps) => {
  const { setPage } = useTablesInput();
  const table = useTable(groupData, identifier, onRefresh);
  const tableGroupData = table.node ?? groupData;
  const isLoadingPage = table.isFetching;
  const refresh = table.refresh;

  // The title row, the same for every section. A table hands in its own
  // actions to sit beside the title; a quote list has none.
  const header = (actions?: ReactNode) => (
    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', my: 1 }}>
      <Typography variant="body1">{tableGroupData.title}</Typography>
      {actions}
    </Stack>
  );

  // Quotes render as cards rather than rows. Dispatching here is the point of
  // QuoteList being a DataDisplay: a group carries quotes the same way it
  // carries tables, and the title and page control around it are shared.
  //
  // A section pages on its stable identifier.
  const section = isQuoteList(tableGroupData)
    ? {
        pageInfo: tableGroupData.page,
        pageKey: tableGroupData.identifier,
        content: <QuoteListView list={tableGroupData} onReload={refresh} />,
        // TableWrapper spaces a table from what's above it the same way.
        header: <Box sx={{ mt: 1 }}>{header()}</Box>,
      }
    : isExpandableTable(tableGroupData)
      ? {
          pageInfo: tableGroupData.pageInfo,
          pageKey: tableGroupData.identifier || tableGroupData.title,
          content: (
            <ExpandableTableView
              table={tableGroupData}
              dataDisplay={dataDisplay}
              screen={screen}
              isRevalidating={isRevalidating}
              onRefresh={refresh}
              onScreenUpdate={onScreenUpdate}
              tabIndex={tabIndex}
              tableIndex={tableIndex}
              tableGroupIndex={tableGroupIndex}
              header={header}
            />
          ),
          // The table draws the title row itself, around its actions.
          header: null,
        }
      : undefined;

  if (!section) return <ChartView dataDisplay={dataDisplay} />;

  const { pageInfo, pageKey } = section;
  return (
    <Stack>
      {section.header}
      {section.content}
      {/* Shown only where there is a second page to go to. The total is
          deprecated and not every screen sends it, so without one the control
          counts open-ended and hasNextPage decides whether next is offered. */}
      {(pageInfo.hasNextPage || pageInfo.page > 0) && (
        <TablePagination
          component="div"
          count={pageInfo.total > 0 ? pageInfo.total : -1}
          page={pageInfo.page}
          rowsPerPage={pageInfo.pageSize || ROWS_PER_PAGE}
          rowsPerPageOptions={[]}
          slotProps={{ actions: { nextButton: { disabled: !pageInfo.hasNextPage } } }}
          onPageChange={(_, next) => setPage(next, pageKey)}
          ActionsComponent={isLoadingPage ? PageLoadingActions : undefined}
        />
      )}
    </Stack>
  );
};

const TableGroup = ({
  screen,
  tableGroup,
  isRevalidating = false,
  tabIndex,
  tableGroupIndex,
  onRefresh,
  onScreenUpdate = () => {},
}: TableGroupProps) => {
  const props = {
    screen,
    tabIndex,
    tableGroupIndex,
    isRevalidating,
    onRefresh,
    onScreenUpdate,
  };

  if (!tableGroup?.data?.length) return null;

  return tableGroup.data.map((data, index, dataDisplay) => (
    <TableGroupContent
      key={data.title}
      {...props}
      identifier={isChart(data) ? '' : data.identifier}
      groupData={data}
      dataDisplay={dataDisplay as ExpandableTableType[]}
      tableIndex={index}
    />
  ));
};

export default TableGroup;
